import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../../lib/baseQueries";
import type {
  IMemberResponse,
  IGetMembersParams,
  IInvitationResponse,
  IGetInvitationsParams,
} from "./Member.types";

interface IInviteMemberParams {
  workspaceId: string;
  inviteeEmail: string;
  role: string;
}

export const memberApi = createApi({
  reducerPath: "memberApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Member", "Invitation"],
  endpoints: (builder) => ({
    getMembers: builder.query<IMemberResponse, IGetMembersParams>({
      query: (params) => ({
        url: `/v1/members?page=${params.page ?? 1}&limit=${params.limit ?? 10}${params.sort ? `&sort=${params.sort}` : ""}${params.order ? `&order=${params.order}` : ""}${params.search ? `&search=${params.search}` : ""}${params.startDate ? `&startDate=${params.startDate}` : ""}${params.endDate ? `&endDate=${params.endDate}` : ""}`,
        method: "POST",
        data: { workspaceIds: params.workspaceIds },
      }),
      providesTags: ["Member"],
    }),
    getInvitations: builder.query<IInvitationResponse, IGetInvitationsParams>({
      query: (params) => ({
        url: `/v1/members/invitations?page=${params.page ?? 1}&limit=${params.limit ?? 10}${params.sort ? `&sort=${params.sort}` : ""}${params.order ? `&order=${params.order}` : ""}${params.search ? `&search=${params.search}` : ""}${params.startDate ? `&startDate=${params.startDate}` : ""}${params.endDate ? `&endDate=${params.endDate}` : ""}`,
        method: "POST",
        data: { workspaceIds: params.workspaceIds },
      }),
      providesTags: ["Invitation"],
    }),
    inviteMember: builder.mutation<{ message: string }, IInviteMemberParams>({
      query: (params) => ({
        url: "/v1/members/invite",
        method: "POST",
        data: params,
      }),
      invalidatesTags: (result, error) =>
        error ? [] : ["Member", "Invitation"],
    }),
    acceptInvite: builder.mutation<
      { message: string },
      { invitationId: string }
    >({
      query: (params) => ({
        url: "/v1/members/accept-invite",
        method: "POST",
        data: params,
      }),
      invalidatesTags: (result, error) =>
        error ? [] : ["Member", "Invitation"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const { workspaceApi } = await import(
            "@/features/Workspace/Workspace.api"
          );
          dispatch(workspaceApi.util.invalidateTags(["Workspace"]));
        } catch {}
      },
    }),
    deleteInvitation: builder.mutation<
      { message: string },
      { invitationId: string }
    >({
      query: (params) => ({
        url: "/v1/members/invite",
        method: "DELETE",
        data: params,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Invitation"]),
    }),
    resendInvitation: builder.mutation<
      { message: string },
      { invitationId: string }
    >({
      query: (params) => ({
        url: "/v1/members/invite/resend",
        method: "POST",
        data: params,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Invitation"]),
    }),
    removeMember: builder.mutation<{ message: string }, { memberId: string }>({
      query: ({ memberId }) => ({
        url: `/v1/members/${memberId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Member"]),
    }),
    updateMemberRole: builder.mutation<
      { message: string },
      { memberId: string; role: string }
    >({
      query: ({ memberId, role }) => ({
        url: `/v1/members/${memberId}/role`,
        method: "PUT",
        data: { role },
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Member"]),
    }),
  }),
});

export const {
  useGetMembersQuery,
  useGetInvitationsQuery,
  useInviteMemberMutation,
  useAcceptInviteMutation,
  useDeleteInvitationMutation,
  useResendInvitationMutation,
  useRemoveMemberMutation,
  useUpdateMemberRoleMutation,
} = memberApi;

